/**
 * EVBreakdownPercentageChart — multi-year EV decomposition as % of total EV/EBITDA multiple.
 *
 * Shows how the EV/EBITDA multiple is split across:
 *   - Current Operations (from EBIT)
 *   - Future Growth
 *   - Market Expectation (if available)
 *
 * Stacked bar chart; years on X-axis. In peer mode, one bar per company.
 */
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import ChartCard, { FocalBadge, focalTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import FinancialNoData from '../FinancialNoData';
import { EV_SEGMENT, BAR_MAX_SIZE, gridProps, xAxisProps, yAxisProps, legendProps, CHART_TYPE } from './chartTheme';
import { formatFiscalYear } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  /**
   * When provided (and non-empty), the chart switches from single-company
   * multi-year decomposition to a one-bar-per-company peer comparison using
   * each company's latest comparable year.
   */
  companyName?: string;
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

function pickLatestComponents(records: UnifiedFinancialRecord[]) {
  const sorted = [...records].sort((a, b) => b.year - a.year);
  for (const r of sorted) {
    const ebit = r.ev_from_ebit_ebitda_multiple ?? r.ev_from_ebit;
    const growth = r.ev_from_future_growth_ebitda_multiple ?? r.ev_from_future_growth;
    const market = r.ev_from_future_market_expectation;
    if (
      (ebit != null || growth != null || market != null) &&
      Math.abs(ebit ?? 0) + Math.abs(growth ?? 0) + Math.abs(market ?? 0) > 0
    ) {
      return { year: r.year, ebit: ebit ?? 0, growth: growth ?? 0, market: market ?? 0 };
    }
  }
  return null;
}

export default function EVBreakdownPercentageChart({
  data,
  region,
  companyName,
  competitorData,
}: Props) {
  const isPeerMode = !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  if (isPeerMode) {
    const focal = pickLatestComponents(data);
    // Insufficient peer data is a feature-not-available case, not a no-data one.
    if (!focal) return null;

    const rows = [
      { name: companyName!, ...focal },
      ...Object.entries(competitorData!)
        .map(([name, records]) => {
          const peer = pickLatestComponents(records);
          if (!peer) return null;
          return { name, ...peer };
        })
        .filter((r): r is { name: string; year: number; ebit: number; growth: number; market: number } => r != null),
    ];
    if (rows.length < 2) return null;

    const hasMarket = rows.some((r) => Math.abs(r.market) > 0);

    const peerChartData = rows.map((r) => {
      const total = Math.abs(r.ebit) + Math.abs(r.growth) + (hasMarket ? Math.abs(r.market) : 0);
      const ebitPct = total !== 0 ? parseFloat(((r.ebit / total) * 100).toFixed(1)) : 0;
      const growthPct = total !== 0 ? parseFloat(((r.growth / total) * 100).toFixed(1)) : 0;
      const marketPct = total !== 0 && hasMarket ? parseFloat(((r.market / total) * 100).toFixed(1)) : 0;
      return {
        year: r.name,
        'Current Ops %': ebitPct,
        'Future Growth %': growthPct,
        ...(hasMarket ? { 'Market Exp %': marketPct } : {}),
      };
    });

    return (
      <ChartCard
        title="EV Composition — Peer Comparison"
        subtitle="Share of EV/EBITDA from current operations, future growth, and market expectations. Latest comparable year per company."
        action={<FocalBadge name={companyName!} />}
      >
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={peerChartData} margin={{ top: 16, right: 16, left: 8, bottom: 8 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="year" interval={0} height={110} tick={focalTickFactory(companyName!)} />
            <YAxis {...yAxisProps} width={44} domain={[0, 100]} tickFormatter={(v) => `${v.toFixed(0)}%`} />
            <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(1)}%`} />} />
            <Legend {...legendProps} verticalAlign="top" align="right" wrapperStyle={{ fontSize: CHART_TYPE.legend, paddingBottom: 8 }} />
            <Bar dataKey="Current Ops %" fill={EV_SEGMENT.ebit} stackId="ev" maxBarSize={BAR_MAX_SIZE} />
            <Bar dataKey="Future Growth %" fill={EV_SEGMENT.growth} stackId="ev" maxBarSize={BAR_MAX_SIZE} />
            {hasMarket && <Bar dataKey="Market Exp %" fill={EV_SEGMENT.market} stackId="ev" maxBarSize={BAR_MAX_SIZE} />}
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  const valid = data.filter(
    (d) => d.ev_from_ebit_ebitda_multiple != null || d.ev_from_future_growth_ebitda_multiple != null
  );
  // Rendered under a SectionDivider — a message keeps the section from dangling.
  if (valid.length === 0) {
    return <FinancialNoData state="metric-missing" metricLabel="EV decomposition" cardTitle="EV Breakdown — % Contribution" />;
  }

  const hasMarket = valid.some((d) => d.ev_from_future_market_expectation != null && d.ev_from_future_market_expectation !== 0);

  const chartData = valid.map((d) => {
    const ebit = d.ev_from_ebit_ebitda_multiple ?? 0;
    const growth = d.ev_from_future_growth_ebitda_multiple ?? 0;
    const market = hasMarket ? (d.ev_from_future_market_expectation ?? 0) : 0;
    const total = ebit + growth + market;
    const ebitPct = total !== 0 ? parseFloat(((ebit / total) * 100).toFixed(1)) : 0;
    const growthPct = total !== 0 ? parseFloat(((growth / total) * 100).toFixed(1)) : 0;
    const marketPct = total !== 0 ? parseFloat(((market / total) * 100).toFixed(1)) : 0;

    return {
      year: formatFiscalYear(d.year, region),
      'Current Ops %': ebitPct,
      'Future Growth %': growthPct,
      ...(hasMarket ? { 'Market Exp %': marketPct } : {}),
    };
  });

  return (
    <ChartCard
      title="EV Breakdown — % Contribution"
      subtitle="EV/EBITDA multiple decomposed: current operations vs future growth vs market expectations"
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" {...xAxisProps} />
          <YAxis {...yAxisProps} width={44} domain={[0, 100]} tickFormatter={(v) => `${v.toFixed(0)}%`} />
          <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(1)}%`} />} />
          <Legend {...legendProps} />
          {/* Only Current Ops carries an in-bar label — its fill clears 4.5:1 for
              white text; the lighter Growth/Market fills would not. */}
          <Bar dataKey="Current Ops %" fill={EV_SEGMENT.ebit} stackId="ev" maxBarSize={BAR_MAX_SIZE}>
            <LabelList
              dataKey="Current Ops %"
              position="inside"
              formatter={(v: number) => (v >= 8 ? `${v.toFixed(0)}%` : '')}
              style={{ fontSize: CHART_TYPE.dataLabel, fill: '#fff', fontWeight: 600 }}
            />
          </Bar>
          <Bar dataKey="Future Growth %" fill={EV_SEGMENT.growth} stackId="ev" maxBarSize={BAR_MAX_SIZE} />
          {hasMarket && <Bar dataKey="Market Exp %" fill={EV_SEGMENT.market} stackId="ev" maxBarSize={BAR_MAX_SIZE} />}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
